'use client';

import type { ReactNode } from 'react';
import type { CustomFormatConfig } from 'react-intl';

import type { DateSerializable } from '@vs-shop/schemas/DateSerializable';
import type { DateTimeSerializable } from '@vs-shop/schemas/DateTimeSerializable';
import { BigDecimal, Option } from 'effect';
import { FormattedDate, FormattedNumber } from 'react-intl';

import { IntlMoneyNew } from './IntlMoney/IntlMoneyNew';
import type { MoneyProps } from './intlFormat';

import { decodeUnknownPriceWithCurrencySync } from '@vs-shop/schemas/primitive/priceWithCurrency';

// Re-export IntlMoneyFromPrice for convenience
export { IntlMoneyFromPrice } from './IntlMoney/IntlMoneyFromPrice';
// The money types and the pure string formatters live in the directive-less
// `intlFormat` module so they stay callable from server components. Re-export the
// types here so existing `import type { MoneyT } from '.../Intl'` sites are unaffected.
export type { MoneyT, MoneyProps } from './intlFormat';

type EmptyT = {
  emptyValue?: ReactNode;
};

type OptionsT = Intl.DateTimeFormatOptions & CustomFormatConfig;

type DateT = Pick<OptionsT, 'year' | 'month' | 'day' | 'weekday'>;

export function IntlDate({
  value,
  emptyValue = null,
  year = 'numeric',
  month = 'numeric',
  day = 'numeric',
  weekday,
}: {
  value: DateSerializable | DateTimeSerializable | null;
} & EmptyT &
  DateT) {
  return Option.match(Option.fromNullishOr(value), {
    onNone: () => emptyValue,
    onSome: (serializable) => (
      <FormattedDate
        value={serializable.value}
        year={year}
        month={month}
        day={day}
        weekday={weekday}
      />
    ),
  });
}

export function IntlMoney({ value }: MoneyProps) {
  const priceWithCurrency = decodeUnknownPriceWithCurrencySync({
    price: value.amount,
    currency: value.currency,
  });

  return (
    <span className="font-heading">
      <IntlMoneyNew value={priceWithCurrency} />
    </span>
  );
}

export function IntlPercentage({
  value,
  minimumFractionDigits = 0,
  maximumFractionDigits = 2,
}: {
  value: string;
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
}) {
  const percentage = parseFloat(value) / 100;

  return (
    <FormattedNumber
      value={percentage}
      minimumFractionDigits={minimumFractionDigits}
      maximumFractionDigits={maximumFractionDigits}
      style="percent"
    />
  );
}

export function IntlDecimal({
  value,
  minimumFractionDigits = 0,
  maximumFractionDigits = 2,
}: {
  value: number;
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
}) {
  return (
    <FormattedNumber
      value={value}
      style="decimal"
      minimumFractionDigits={minimumFractionDigits}
      maximumFractionDigits={maximumFractionDigits}
    />
  );
}

export function IntlBigDecimal({
  value,
  minimumFractionDigits = 0,
  maximumFractionDigits = 2,
}: {
  value: BigDecimal.BigDecimal;
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
}) {
  return (
    <FormattedNumber
      value={BigDecimal.toNumberUnsafe(value)}
      style="decimal"
      minimumFractionDigits={minimumFractionDigits}
      maximumFractionDigits={maximumFractionDigits}
    />
  );
}
